// ============================================================
// THE ACTING LEADER, ASKED ONCE (Squad Mode stage 7, ruled 2026-10-23):
// when a kill-team's brothers on a stage are two or more and their
// leader is not among them, the GM - alone - names who leads them there.
// The answer is kept for that exact set on that stage (`stageKey`), so the
// question comes back only when the set changes (a brother arrives or
// leaves) or the one named is no longer among them.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { brothersIn, needsActingLeader, stageKey, withActingLeader, type KillTeams, type TeamsDoc } from './record';
import { teamOf } from '../roster/team';

export interface LeaderAsk {
  tag: string;
  /** The team's title, as its brothers wear it. */
  label: string;
  /** Where the answer is kept. */
  key: string;
  /** The leader's name, if he is on the roster at all (elsewhere: the wings). */
  leaderName: string | null;
  brothers: { id: string; name: string }[];
}

/** Every question owed on this stage, in the roster's order of teams. */
export function leaderAsks(roster: readonly Combatant[], teams: KillTeams, sceneId: string | null): LeaderAsk[] {
  return Object.values(teams.killTeams).flatMap((kt) => {
    const here = brothersIn(roster, kt.tag);
    const ids = here.map((c) => c.id);
    // A kill-team with no Oath has no Squad Mode to lead.
    if (!kt.oath || !needsActingLeader(kt, sceneId, ids)) return [];
    const label = teamOf(here[0])?.label || kt.tag;
    const leaderName = roster.find((c) => c.id === kt.leaderId)?.name ?? null;
    const brothers = here.map((c) => ({ id: c.id, name: c.name }));
    return [{ tag: kt.tag, label, key: stageKey(sceneId, ids), leaderName, brothers }];
  });
}

/** The GM's answer, kept under the question's key in the session's document. */
export const answerAsk = (doc: TeamsDoc, ask: LeaderAsk, leaderId: string): TeamsDoc =>
  withActingLeader(doc, ask.tag, ask.key, leaderId);
